import Add from '@mui/icons-material/Add';
import { paperClasses } from '@mui/material';
import Box from '@mui/material/Box';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import AutocompleteRutterTaxRatesTypeahead from '~components/FormFields/CommonAutocompletes/AutocompleteRutterTaxRatesTypeahead';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { DEFAULT_API_PAGE_SIZE } from '~constants/consts';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { RutterMappedRecord } from '~hooks/useRutter/models';
import useRutter from '~hooks/useRutter/useRutter';
import { TaxRateSchedule } from '~hooks/useTaxRateSchedule/models';
import useTaxRateSchedule from '~hooks/useTaxRateSchedule/useTaxRateSchedule';
import TaxRateScheduleForm, {
  TaxRateScheduleFormHandler,
} from '~pages/Settings/TaxRateScheduleManagement/TaxRateScheduleForm';
import {
  TaxRateScheduleDto,
  TaxRateScheduleDtoAsyncError,
} from '~pages/Settings/TaxRateScheduleManagement/taxRateScheduleSchema';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { MAPPING_COLUMN_MIN_WIDTH_PX } from './mappingColumnWidth';
import { useQuickBooksMappingQueries } from './QuickBooksMappingQueryContext';
import QuickBooksTypeaheadCell from './QuickBooksTypeaheadCell';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online';

interface MappedTaxRateSchedule {
  mappedRecord: Nullable<RutterMappedRecord>;
  taxRateSchedule: TaxRateSchedule;
}

interface TaxRateMappingRow extends MappedTaxRateSchedule {
  id: string;
}

// "13%" / "6.875%"; a schedule without a rate renders blank
function formatRate(rate: Nullable<string | number>) {
  if (rate === null || rate === undefined || rate === '') {
    return '';
  }

  return `${Number(rate)}%`;
}

export default function TaxRatesTab() {
  const { connectionId, isLoading, saveTaxRateMapping } = useQuickBooksMappingQueries();
  const { toasterStore, userStore } = useStores();
  const { createTaxRateSchedule } = useTaxRateSchedule();
  const { getTaxRateSchedulesPage } = useRutter();
  const { canCreateRate } = userStore.getPermissions();
  const companyId = getEffectiveUserCompanyId(userStore);

  const [searchValue, setSearchValue] = useState('');
  const {
    goToPage,
    isFetching,
    pagination,
    reloadCurrentPage,
    startOver,
    rows: mappedTaxRateSchedules,
  } = useCursorPagination<MappedTaxRateSchedule>({
    fetchPage: ({ cursor, limit, link, signal }) =>
      getTaxRateSchedulesPage({
        connectionId: String(connectionId),
        cursor,
        limit,
        link,
        search: searchValue,
        signal,
      }),
    limit: DEFAULT_API_PAGE_SIZE,
  });

  useEffect(() => {
    if (connectionId) {
      void startOver();
    }
  }, [connectionId, searchValue]);

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createAsyncErrors, setCreateAsyncErrors] = useState<
    TaxRateScheduleDtoAsyncError[]
  >([]);
  const taxRateScheduleFormRef = useRef<TaxRateScheduleFormHandler>(null);

  // in-flight submission guard (COMP-32): the dialog header's close button and Escape
  // both call this directly, so the no-op while creating has to live here rather than
  // only on the action row's Cancel button
  const closeCreateDialog = useCallback(() => {
    if (isCreating) {
      return;
    }

    setIsCreateDialogOpen(false);
    setCreateAsyncErrors([]);
  }, [isCreating]);

  // same flow as Settings → Tax Rate Schedules: the form owns validation, a 422 on the
  // name (the only field the API validates per company) is shown inline, anything else
  // has already been toasted by the hook
  const handleCreateSubmit = useCallback(async () => {
    const formData = await taxRateScheduleFormRef.current?.onSubmit();
    if (!formData) {
      return;
    }

    setCreateAsyncErrors([]);
    setIsCreating(true);

    try {
      const created = await createTaxRateSchedule(
        formData.company.value,
        TaxRateSchedule.deparseCreate({
          name: formData.name,
          rate: formData.rate,
          externalAccountingId: formData.externalAccountingId,
          externalId: formData.externalId,
        }),
      );
      toasterStore.push(
        alert(
          t('administration.tax_rate_schedule.created', { name: created.name }),
          AlertTypes.success,
        ),
      );
      setIsCreateDialogOpen(false);
      await goToPage();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        const fieldMap: Record<string, keyof TaxRateScheduleDto> = { name: 'name' };
        const errorsArray = (error.response.data?.error?.errors ??
          []) as ModelError_Item[];

        setCreateAsyncErrors(
          errorsArray.flatMap(({ field, message }) => {
            const formField = fieldMap[field];

            return formField ? [{ field: formField, message }] : [];
          }),
        );
      }
    } finally {
      setIsCreating(false);
    }
  }, [createTaxRateSchedule, goToPage, toasterStore]);

  const rows: TaxRateMappingRow[] = useMemo(
    () =>
      mappedTaxRateSchedules.map((mappedTaxRateSchedule) => ({
        ...mappedTaxRateSchedule,
        id: mappedTaxRateSchedule.taxRateSchedule.id,
      })),
    [mappedTaxRateSchedules],
  );

  const handleSave = useCallback(
    async (row: TaxRateMappingRow, nextQuickBooksRecordId: Nullable<string>) => {
      const saved = await saveTaxRateMapping(
        row.id,
        row.mappedRecord?.id ?? null,
        nextQuickBooksRecordId,
      );

      if (saved) {
        toasterStore.push(
          alert(
            t(`${I18N_PREFIX}.grid.tread_name_updated`, {
              name: row.taxRateSchedule.name,
            }),
            AlertTypes.success,
          ),
        );
      }

      await reloadCurrentPage();

      return saved;
    },
    [reloadCurrentPage, saveTaxRateMapping, toasterStore],
  );

  const columns: Array<DataTableColumn<TaxRateMappingRow>> = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: t(`${I18N_PREFIX}.tread_first.columns.tread_tax_rate_schedule`),
        valueGetter: (row) => row.taxRateSchedule.name,
      },
      {
        field: 'rate',
        headerName: t(`${I18N_PREFIX}.tread_first.columns.rate`),
        valueGetter: (row) => formatRate(row.taxRateSchedule.rate),
      },
      {
        field: 'externalAccountingId',
        headerName: t(`${I18N_PREFIX}.tread_first.columns.accounting_id`),
        valueGetter: (row) => row.taxRateSchedule.externalAccountingId ?? '',
      },
      {
        field: 'mapping',
        headerName: t(`${I18N_PREFIX}.tread_first.columns.quick_books_tax_rate`),
        minWidth: MAPPING_COLUMN_MIN_WIDTH_PX,
        renderCell: (row) => (
          <QuickBooksTypeaheadCell
            dataTestId={`quick-books-mapping-select-${row.id}`}
            onSave={(nextQuickBooksRecordId) => handleSave(row, nextQuickBooksRecordId)}
            value={row.mappedRecord}
            renderTypeahead={(typeaheadProps) => (
              <AutocompleteRutterTaxRatesTypeahead
                {...typeaheadProps}
                companyId={companyId}
                connectionId={String(connectionId)}
              />
            )}
          />
        ),
      },
    ];
  }, [companyId, connectionId, handleSave]);

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <SmallButton
          color="brandV2Yellow"
          disabled={!canCreateRate}
          onClick={() => setIsCreateDialogOpen(true)}
          startIcon={<Add />}
        >
          {t(`${I18N_PREFIX}.tread_first.new_tax_rate_schedule`)}
        </SmallButton>
      </Box>

      <DataTable
        columns={columns}
        headerTrailingComponent={
          <DataGridPager
            isFetching={isFetching}
            onNavigate={(link) => void goToPage(link)}
            pagination={pagination}
          />
        }
        id="quick-books-mapping-tax-rates-grid"
        loading={isLoading || isFetching}
        onSearchChange={setSearchValue}
        rows={rows}
      />

      <Dialog
        isOpen={isCreateDialogOpen}
        onClose={closeCreateDialog}
        preventCloseOnBackdropClick
        sx={{ [`.${paperClasses.root}`]: { maxWidth: '700px' } }}
      >
        <DialogHeader onClose={closeCreateDialog}>
          {t('administration.tax_rate_schedule.create')}
        </DialogHeader>

        <DialogContent>
          <TaxRateScheduleForm
            ref={taxRateScheduleFormRef}
            asyncErrors={createAsyncErrors}
            isCompanyFieldLocked
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={isCreating}
            onClick={closeCreateDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="primary"
            disabled={isCreating}
            loading={isCreating}
            onClick={handleCreateSubmit}
          >
            {t('actions.create')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
